
// Utility functions for extracting and processing Google Maps URLs

export const extractMapPreviews = (text: string, context?: 'generation' | 'reveal' | 'feedback') => {
  console.log('🗺️ mapUtils: Analyzing text for Google Maps URLs');
  console.log('🗺️ mapUtils: Context:', context);
  console.log('🗺️ mapUtils: Full text content:', text);
  
  // CRITICAL FIX: Don't show map previews during target generation or feedback
  if (context === 'generation' || context === 'feedback') {
    console.log(`🗺️ mapUtils: Skipping map preview extraction during ${context}`);
    return {
      text: text,
      mapUrls: []
    };
  }
  
  // ENHANCED: More comprehensive patterns to catch Google Maps URLs
  const patterns = [
    // Standard Google Maps URLs with @ coordinates
    /(https?:\/\/(?:www\.)?google\.com\/maps\/@[-\d.,]+[^\s\)\]\<\>\"\']*)/gi,
    // Direct cbll format: https://maps.google.com/?cbll=lat,lng
    /(https?:\/\/maps\.google\.com\/\?cbll=[-\d.,]+[^\s\)\]\<\>\"\']*)/gi,
    // Your specific format: https://maps.google.com/maps?q=&layer=c&cbll=lat,lng&cbp=...
    /(https?:\/\/maps\.google\.com\/maps\?[^&]*q=[^&]*&layer=c&cbll=[-\d.,]+[^\s\)\]\<\>\"\']*)/gi,
    // Alternative format without q parameter: https://maps.google.com/maps?layer=c&cbll=lat,lng
    /(https?:\/\/maps\.google\.com\/maps\?layer=c&cbll=[-\d.,]+[^\s\)\]\<\>\"\']*)/gi,
    // More flexible pattern for any maps.google.com URL with cbll parameter
    /(https?:\/\/maps\.google\.com\/[^?\s]*\?[^?\s]*cbll=[-\d.,]+[^\s\)\]\<\>\"\']*)/gi,
    // Standard Google Maps URLs with various parameters
    /(https?:\/\/(?:www\.)?google\.com\/maps[^\s\)\]\<\>\"\']*)/gi,
    // Google Maps URLs in markdown format [text](url)
    /\[([^\]]*)\]\((https?:\/\/(?:www\.)?google\.com\/maps[^\)]*)\)/gi,
    // Maps.app.goo.gl short URLs
    /(https?:\/\/maps\.app\.goo\.gl\/[^\s\)\]\<\>\"\']*)/gi,
    // Goo.gl short URLs
    /(https?:\/\/goo\.gl\/maps\/[^\s\)\]\<\>\"\']*)/gi
  ];
  
  let allMapUrls = [];
  
  // Try each pattern
  for (let i = 0; i < patterns.length; i++) {
    const pattern = patterns[i];
    console.log(`🗺️ mapUtils: Testing pattern ${i + 1}:`, pattern);
    
    let matches;
    while ((matches = pattern.exec(text)) !== null) {
      console.log(`🗺️ mapUtils: Pattern ${i + 1} found match:`, matches);
      
      // For markdown format, take the URL part (index 2), otherwise take the full match (index 1 or 0)
      const url = matches[2] || matches[1] || matches[0];
      if (url) {
        // ENHANCED: Validate that this looks like a proper Google Maps URL
        if (url.includes('google.com/maps') || url.includes('maps.google.com') || url.includes('maps.app.goo.gl')) {
          allMapUrls.push(url);
          console.log('🗺️ mapUtils: Added valid Google Maps URL:', url);
        } else {
          console.log('🗺️ mapUtils: Rejected invalid URL:', url);
        }
      }
    }
    
    // Reset regex lastIndex for global regex
    pattern.lastIndex = 0;
  }
  
  console.log('🗺️ mapUtils: All found URLs before deduplication:', allMapUrls);
  
  // Remove duplicate URLs
  const uniqueMapUrls = [...new Set(allMapUrls)];
  
  console.log('🗺️ mapUtils: Unique map URLs to render:', uniqueMapUrls);
  
  return {
    text: text,
    mapUrls: uniqueMapUrls
  };
};

// Enhanced Street View detection function
export const isStreetViewUrl = (url: string): boolean => {
  console.log('🗺️ mapUtils: Checking if URL is Street View:', url);
  
  const streetViewIndicators = [
    'layer=c',           // Street View layer parameter
    'cbll=',             // Street View coordinates
    'data=!3m7!1e1',     // Street View data format
    'data=!3m1!1e3',     // Alternative Street View data format
    '!1e1',              // Street View indicator in data
    '3a,',               // Street View in @ format
    'streetview'         // Direct streetview keyword
  ];
  
  // Special case for @ coordinates with data
  const hasAtWithData = url.includes('@') && url.includes(',') && url.includes('data=');
  
  const isStreetView = streetViewIndicators.some(indicator => url.includes(indicator)) || hasAtWithData;
  console.log('🗺️ mapUtils: Street View detection result:', isStreetView);
  
  return isStreetView;
};

// Extract coordinates from Street View URL
export const getStreetViewCoords = (url: string) => {
  console.log('🗺️ mapUtils: Extracting coordinates from URL:', url);
  
  // Handle @lat,lng format (most common)
  const atMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (atMatch) {
    const coords = { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };
    console.log('🗺️ mapUtils: Found coordinates via @ pattern:', coords);
    return coords;
  }
  
  // Handle cbll=lat,lng format (street view)
  const cbllMatch = url.match(/cbll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (cbllMatch) {
    const coords = { lat: parseFloat(cbllMatch[1]), lng: parseFloat(cbllMatch[2]) };
    console.log('🗺️ mapUtils: Found coordinates via cbll pattern:', coords);
    return coords;
  }
  
  // Handle ll=lat,lng format
  const llMatch = url.match(/ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (llMatch) {
    const coords = { lat: parseFloat(llMatch[1]), lng: parseFloat(llMatch[2]) };
    console.log('🗺️ mapUtils: Found coordinates via ll pattern:', coords);
    return coords;
  }
  
  // Handle query parameter format
  const queryMatch = url.match(/q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (queryMatch) {
    const coords = { lat: parseFloat(queryMatch[1]), lng: parseFloat(queryMatch[2]) };
    console.log('🗺️ mapUtils: Found coordinates via q pattern:', coords);
    return coords;
  }
  
  console.log('🗺️ mapUtils: No coordinates found in URL');
  return null;
};

// Extract Street View parameters (heading, pitch, fov, panoId)
export const getStreetViewParams = (url: string) => {
  let heading = 0;
  let pitch = 0;
  let fov = 90;
  let panoId = undefined;

  console.log('🗺️ mapUtils: Extracting Street View params from URL:', url);

  // Extract from @lat,lng,3a,75y,139t format (most common)
  const streetViewMatch = url.match(/@[\d.-]+,[\d.-]+,(\d+(?:\.\d+)?)a,(\d+(?:\.\d+)?)y,(\d+(?:\.\d+)?)t/);
  if (streetViewMatch) {
    fov = parseFloat(streetViewMatch[2]) || 90; // The 'y' value
    heading = parseFloat(streetViewMatch[3]) || 0; // The 't' value
    pitch = 0; // Usually 0 for street view
    console.log('🗺️ mapUtils: Found params via @lat,lng format:', { fov, heading, pitch });
  }

  // Extract panorama ID from data=!3m6!1e1!3m4!1s[panoId]!2e0!7i16384!8i8192
  const panoMatch = url.match(/!1s([^!]+)!/);
  if (panoMatch && panoMatch[1] !== '0x0:0x0') {
    panoId = panoMatch[1];
    console.log('🗺️ mapUtils: Found panorama ID:', panoId);
  }

  console.log('🗺️ mapUtils: Final Street View params:', { heading, pitch, fov, panoId });
  return { heading, pitch, fov, panoId };
};
